import React, { useState, useEffect, useMemo, useRef } from "react";

// Each reasoning step starts with a bold headline on its own line. Only complete `**...**` pairs
// are matched, because the text arrives token by token and an unclosed pair would show half a word.
const HEADLINE_PATTERN = /^[ \t]*(?:#{1,6}[ \t]*)?\*\*(.+?)\*\*/gm;

// A headline is only a few words. Anything longer is a full paragraph written in bold, which does
// not fit on a single line.
const MAX_HEADLINE_LENGTH = 90;

// Shown until the first headline arrives, and when the headlines received are not readable.
const FIRST_ANSWER_HEADLINES = [
  "Getting oriented…",
  "Thinking…",
  "Pulling up relevant records…",
  "Looking under a few rocks…",
  "Checking the details…",
  "Putting this together…",
];

// Ollie answers questions about permits and plan review, so its placeholder messages describe
// that kind of work instead of field inspection work.
export const OLLIE_HEADLINES = [
  "Looking into this…",
  "Thinking…",
  "Checking the adopted code…",
  "Reading the jurisdiction's rules…",
  "Putting this together…",
];

// Follow-up answers skip the first message of the list. Showing the same opening message before
// every answer looks like the assistant restarted the conversation.
const followUpHeadlines = (headlines: string[]): string[] =>
  headlines.length > 1 ? headlines.slice(1) : headlines;

const FALLBACK_ROTATION_MS = 3500;

// The model sometimes repeats the same word for a whole step and sends a headline like
// "shame shame shame shame". Those are rejected so a fallback message is shown instead.
const isReadableHeadline = (headline: string): boolean => {
  if (!headline || headline.length > MAX_HEADLINE_LENGTH) return false;

  const words = headline.toLowerCase().match(/[\p{L}\p{N}]+/gu);
  if (!words?.length) return false;

  const distinct = new Set(words).size;
  return distinct === words.length || distinct / words.length > 0.6;
};

export const readHeadlines = (thoughts: string): string[] => {
  const headlines: string[] = [];

  for (const [, headline] of thoughts.matchAll(HEADLINE_PATTERN)) {
    const cleaned = headline.replace(/[:\s]+$/, "").trim();
    if (isReadableHeadline(cleaned)) headlines.push(cleaned);
  }

  return headlines;
};

interface StreamingThoughtsProps {
  thoughts: string;
  hasSystemMessage?: boolean;
  isFirstAnswer?: boolean;
  // Messages shown before the model sends its first headline. Each assistant passes its own list
  // so the wording matches the assistant the user is talking to.
  placeholderHeadlines?: string[];
}

export const StreamingThoughts: React.FC<StreamingThoughtsProps> = ({
  thoughts,
  hasSystemMessage = false,
  isFirstAnswer = false,
  placeholderHeadlines = FIRST_ANSWER_HEADLINES,
}) => {
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const hasEverHadSystemMessageRef = useRef(false);

  // Immediately track if we've ever had a system message
  if (hasSystemMessage) {
    hasEverHadSystemMessageRef.current = true;
  }

  // The user only sees the headline, and only until the answer starts arriving. The full reasoning
  // text is never displayed.
  const isLive = !hasEverHadSystemMessageRef.current;

  const headlines = useMemo(() => readHeadlines(thoughts), [thoughts]);
  const currentHeadline = headlines[headlines.length - 1];

  const fallbackHeadlines = isFirstAnswer
    ? placeholderHeadlines
    : followUpHeadlines(placeholderHeadlines);

  const isShowingFallback = isLive && !currentHeadline;
  const isLastFallback = fallbackIndex >= fallbackHeadlines.length - 1;

  // The first step can take a long time to arrive, so the list does not loop: it advances to the
  // last message and stays there instead of repeating the same messages.
  useEffect(() => {
    if (!isShowingFallback || isLastFallback) return;

    const next = window.setTimeout(
      () => setFallbackIndex((index) => index + 1),
      FALLBACK_ROTATION_MS
    );

    return () => window.clearTimeout(next);
  }, [isShowingFallback, isLastFallback, fallbackIndex]);

  if (!isLive) return null;

  const activityLabel = currentHeadline ?? fallbackHeadlines[fallbackIndex];

  return (
    <div className="streaming-thoughts-activity" aria-live="polite">
      {/* The key forces React to remount the span, so a new headline animates in. */}
      <span key={activityLabel} className="streaming-thoughts-headline">
        {activityLabel}
      </span>
    </div>
  );
};
